import { TokenProvider } from '~/hooks/auth/useAccessTokenProvider';
import { RequestParameters } from './backend-client';
import { BackendV2Client } from './backend-v2-client';
import { APIType } from './types';
import { handleError } from '~/utils/errors';

export {
    DEFAULT_POLL_INTERVAL_MS,
    DEFAULT_TIMEOUT_MS,
} from './backend-v2-client';

export type CreateRegistrationTaskPayload = {
    task_id: string;
    model_name: string;
    source_image: string;
    target_image: string;
    matrix: number[];
    matrix_shape: number[];
    matrix_convention: 'row_first' | 'column_first';
};

export class RegistrationClient extends BackendV2Client {
    protected get taskPathSegment(): string {
        return 'reg';
    }

    constructor(
        baseURL: string,
        tokenProvider: TokenProvider,
        sessionID: string
    ) {
        super(baseURL, tokenProvider, sessionID, APIType.REGISTRATION);
    }

    /**
     * Creates an adapt+ / registration task with pre-uploaded scans.
     * POSTs `/v2/reg/` with a transformation matrix alongside the two
     * dataset IDs the scans live under.
     */
    public async createRegistrationTask(
        request: RequestParameters,
        payload: CreateRegistrationTaskPayload
    ): Promise<unknown> {
        try {
            // One attempt (the POST default): whether the backend dedupes
            // on the client-generated task_id cannot be verified from
            // here, and a duplicate would start a second registration run
            // the client does not track.
            const response = await this.request('v2/reg/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    skip_dicom_validation: 'true',
                },
                buildBody: () => JSON.stringify(payload),
                params: { sessionId: request.sessionId ?? this.sessionID },
            });

            if (!response.ok) {
                const text = await response.text();
                handleError(
                    new Error(
                        `Failed to create registration task: ${response.status}`
                    ),
                    text
                );
                return false;
            }

            return response.json();
        } catch (error) {
            this.reportRequestFailure(
                error,
                'Error creating task with pre-uploaded scans.'
            );
            return false;
        }
    }
}
